// App Initialization Hook

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { workspaceApi } from '../../apiClients/workspace/workspaceApi';
import { parseWorkspaceSlugFromUrl } from '../../lib/workspaceUtils';
import { rootStore } from '../../stores';
import { useTelegramAuth } from './useTelegramAuth';

// Quickfix for problem when deeplink breaks navigation and actions like
// confirm/cancel participation etc.
// Still not helping with page reload
// TODO FIX ASAP
let deeplinkProcessed = false;

/**
 * Parses deeplink parameter from Telegram start_param
 */
const parseDeeplink = (
  deeplink: string
): { mode: 'day' | 'week' | 'month'; date: string } | null => {
  try {
    const [mode, date] = deeplink.split('_');

    if (!mode || !date) {
      console.warn('Invalid deeplink format:', deeplink);
      return null;
    }

    if (!['day', 'week', 'month'].includes(mode)) {
      console.warn('Invalid calendar mode in deeplink:', mode);
      return null;
    }

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(date)) {
      console.warn('Invalid date format in deeplink:', date);
      return null;
    }

    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) {
      console.warn('Invalid date in deeplink:', date);
      return null;
    }

    return {
      mode: mode as 'day' | 'week' | 'month',
      date,
    };
  } catch (error) {
    console.error('Error parsing deeplink:', error);
    return null;
  }
};

/**
 * Parses start_param string into key-value pairs
 */
const parseStartParams = (startParam: string): Record<string, string> => {
  try {
    return Object.fromEntries(
      startParam.split('&').map((param) => {
        const [key, value] = param.split('=');
        return [key, decodeURIComponent(value || '')];
      })
    );
  } catch (error) {
    console.error('Error parsing start params:', error);
    return {};
  }
};

export function useAppInitialization() {
  const navigate = useNavigate();
  const { isAuthenticating, isAuthenticated } = useTelegramAuth();
  const [isInitializing, setIsInitializing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  // const [deeplinkProcessed, setDeeplinkProcessed] = useState(false);

  useEffect(() => {
    const initializeApp = async () => {
      // Step 1: Wait for authentication to complete
      if (isAuthenticating) {
        return;
      }

      // If authentication failed, don't proceed
      if (!isAuthenticated) {
        return;
      }

      // Step 2: Get workspaces by auth profile
      try {
        setIsInitializing(true);

        const response = await workspaceApi.getWorkspacesByAuthProfile();
        const workspaces = response.workspaces;

        if (!workspaces || workspaces.length === 0) {
          setIsInitializing(false);
          return;
        }

        // Step 3: Get first workspace slug
        const firstWorkspace = workspaces[0];
        const firstWorkspaceSlug = firstWorkspace.slug;

        // Step 4: Check for Telegram deeplink and handle it first
        let shouldHandleDeeplink = false;
        let deeplinkTarget = '';

        if (
          !deeplinkProcessed &&
          typeof window !== 'undefined' &&
          'Telegram' in window &&
          'WebApp' in window.Telegram
        ) {
          const telegram = window.Telegram.WebApp;
          const startParam = telegram.initDataUnsafe.start_param;

          if (startParam) {
            const params = parseStartParams(startParam);
            const deeplink = params.deeplink;

            if (deeplink) {
              const parsedDeeplink = parseDeeplink(deeplink);

              if (parsedDeeplink) {
                const { mode, date } = parsedDeeplink;
                deeplinkTarget = `/w/${firstWorkspaceSlug}/calendar/${mode}/${date}`;
                shouldHandleDeeplink = true;
                // setDeeplinkProcessed(true);
                deeplinkProcessed = true;
              }
            }
          }
        }

        // Step 5: Check current URL and redirect if needed
        const currentSlug = parseWorkspaceSlugFromUrl();

        if (shouldHandleDeeplink) {
          // Navigate to deeplink target instead of default workspace
          navigate(deeplinkTarget, { replace: true });
          // Continue with initialization after navigation
        } else if (currentSlug !== firstWorkspaceSlug) {
          navigate(`/w/${firstWorkspaceSlug}`);
          // Don't initialize yet, wait for next render cycle
          setIsInitializing(false);
          return;
        }

        // Step 6: Initialize store and load data
        await rootStore.initialize(navigate);

        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize app:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    if (isInitialized) {
      return;
    }

    initializeApp();
  }, [isAuthenticating, isAuthenticated, navigate, deeplinkProcessed]);

  return {
    isAuthenticating,
    isAuthenticated,
    isInitializing,
    isInitialized,
  };
}
